import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Container, Title } from './AddCompanyToModelModal.styled';
import { Modal } from '../Modal/Modal';
import { useTranslation } from 'react-i18next';
import ScrollableList from 'components/ScrollableList/ScrollableList';
import { ICompany } from 'types/Company.types';
import { useDebounce } from 'utils/hooks/useDebounce';
import { ELIGIBLE_COMPANIES_FOR_MODEL_PER_PAGE } from './constants';
import { getDataFromPages } from 'utils/getDataFromPages';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { useAddCompanyToModel, useGetCompaniesEligibleForModel } from './hooks';

export interface IAddCompanyToModelModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  modelId: number;
  companyId: number;
}

export function AddCompanyToModelModal({
  isOpen,
  setIsOpen,
  modelId,
  companyId,
}: IAddCompanyToModelModalProps) {
  const { t } = useTranslation();
  const [searchBy, setSearchBy] = useState<string>('');
  const debouncedSearchByCompany = useDebounce(searchBy);

  const {
    data: companiesData,
    isLoading: isLoadingGetCompanies,
    hasNextPage,
    fetchNextPage,
  } = useGetCompaniesEligibleForModel(
    ELIGIBLE_COMPANIES_FOR_MODEL_PER_PAGE,
    modelId,
    companyId,
    debouncedSearchByCompany,
    isOpen
  );

  const companies: ICompany[] = getDataFromPages(
    companiesData?.pages,
    'companies'
  );

  const { mutate: addCompanyToModel, isLoading: isLoadingAddCompanyToModel } =
    useAddCompanyToModel(companyId, modelId);

  const handleSelect = (company: ICompany) => {
    if (!isLoadingAddCompanyToModel) {
      addCompanyToModel(company.id, { onSuccess: () => setIsOpen(false) });
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSearchBy('');
    }
  }, [isOpen]);

  const { isSmallPhone } = useBreakpointFlag();

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      modalStyle={{
        margin: 'auto',
        width: isSmallPhone ? '90%' : '600rem',
        minWidth: isSmallPhone ? '90%' : 'auto',
        position: 'fixed',
      }}
    >
      <Container>
        <Title>{t('Select company for granting access')}</Title>
        <ScrollableList
          items={companies || []}
          isLoading={isLoadingGetCompanies || isLoadingAddCompanyToModel}
          noContentLabel={t('No companies found')}
          onClick={(company: ICompany) => handleSelect(company)}
          onSearch={(str: string) => setSearchBy(str)}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetching={isLoadingAddCompanyToModel}
        />
      </Container>
    </Modal>
  );
}
